import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, FormControl, FormHelperText, FormLabel, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, Textarea, useColorModeValue,
} from "@chakra-ui/react";
import { toggleWalletFreeze } from "../groupsApi";
import { InfoBanner } from "../groupsUi";

/**
 * Submits the freeze toggle and surfaces toasts.
 * @param {Object} args - Submission args
 */
async function submitToggle({ groupId, isFreezing, reason, setLoading, onClose, onToggled }) {
    setLoading(true);
    try {
        const payload = isFreezing && reason?.trim() ? { reason: reason.trim() } : {};
        await toggleWalletFreeze(groupId, payload);
        toast.success(isFreezing ? "Wallet gelé" : "Wallet dégelé");
        onToggled?.();
        onClose();
    } catch (error) {
        const msg = error?.response?.data;
        toast.error(typeof msg === "string" ? msg : "Échec de l'action");
    } finally {
        setLoading(false);
    }
}

/**
 * Reason input shown only when freezing.
 * @param {Object} props - { reason, setReason }
 */
function ReasonInput({ reason, setReason }) {
    return (
        <FormControl>
            <FormLabel fontSize="0.8rem">Raison (optionnelle)</FormLabel>
            <Textarea size="sm" borderRadius="8px" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            <FormHelperText fontSize="0.7rem">Affichée dans les logs admin et sur la bannière du wallet.</FormHelperText>
        </FormControl>
    );
}

/**
 * Body of the freeze toggle dialog.
 * @param {Object} props - { isFreezing, reason, setReason }
 */
function DialogBody({ isFreezing, reason, setReason }) {
    const textColor = useColorModeValue("secondaryGray.800", "whiteAlpha.900");
    return (
        <Stack spacing="16px">
            <InfoBanner>
                {isFreezing
                    ? "Geler le wallet bloquera tout débit (virement, dispatch). Les crédits restent autorisés."
                    : "Dégeler le wallet réautorisera les débits."}
            </InfoBanner>
            <Text color={textColor} fontSize="0.85rem">Confirmer {isFreezing ? "le gel" : "le dégel"} du wallet ?</Text>
            {isFreezing && <ReasonInput reason={reason} setReason={setReason} />}
        </Stack>
    );
}

/**
 * Dialog allowing admins to freeze or unfreeze a group's wallet.
 * @param {Object} props - { open, groupId, wallet, onClose, onToggled }
 */
function FreezeWalletDialog({ open, groupId, wallet, onClose, onToggled }) {
    const isFreezing = !wallet?.frozen;
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setReason(""); }, [open]);

    const onSubmit = () => submitToggle({ groupId, isFreezing, reason, setLoading, onClose, onToggled });

    return (
        <Modal isOpen={open} onClose={onClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{isFreezing ? "Geler le wallet" : "Dégeler le wallet"}</ModalHeader>
                <ModalCloseButton />
                <ModalBody><DialogBody isFreezing={isFreezing} reason={reason} setReason={setReason} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="brand" onClick={onSubmit} isLoading={loading} loadingText="Action...">
                        {isFreezing ? "Geler" : "Dégeler"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default FreezeWalletDialog;
